/**
 * Login Page for Y GO Shuttle Services France
 * Premium dark theme design with OTP verification
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, LogIn, AlertCircle, ArrowLeft, Home } from 'lucide-react';
import OTPVerificationModal from '../components/OTPVerificationModal';
import config from 'config';

const API_URL = config.API_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { loginWithOTP } = useAuth();
  const { logoUrl, siteTitle } = useBranding();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // OTP state
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otpEmail, setOtpEmail] = useState(''); // Email complet pour la vérification

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Get device token if exists
      const deviceToken = localStorage.getItem('ygo_device_token');
      
      const response = await fetch(`${API_URL}/api/auth/otp/login/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.toLowerCase(),
          password,
          device_token: deviceToken
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || data.message || 'Email ou mot de passe incorrect');
        return;
      }

      if (data.status === 'authenticated') {
        // Trusted device - direct login
        handleLoginSuccess(data);
      } else if (data.status === 'otp_required') {
        // Need OTP verification
        setMaskedEmail(data.email);
        setOtpEmail(email.toLowerCase()); // Conserver l'email complet
        setShowOTPModal(true);
      }
    } catch (err) {
      console.error('Login fetch error:', err);
      // Si erreur réseau ou parsing JSON
      if (err.name === 'SyntaxError') {
        setError('Erreur de réponse du serveur');
      } else {
        setError('Email ou mot de passe incorrect');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (data) => {
    // Save device token if provided
    if (data.device_token) {
      localStorage.setItem('ygo_device_token', data.device_token);
    }
    
    // Use loginWithOTP to properly set auth state (with refresh_token)
    loginWithOTP(data.token, data.user, data.refresh_token);
    
    // Redirect based on role
    const roles = data.user.roles || [];
    const hasRole = (name) => roles.some(r => r.name === name || r === name);

    if (hasRole('SUPERADMIN') || hasRole('ADMIN') || hasRole('DISPATCHER')) {
      navigate('/admin');
    } else if (hasRole('PARTNER')) {
      navigate('/partner/dashboard');
    } else if (hasRole('CHAUFFEUR') || hasRole('DRIVER')) {
      navigate('/app');
    } else {
      navigate('/client');
    }
  };

  const handleOTPSuccess = (data) => {
    setShowOTPModal(false);
    handleLoginSuccess(data);
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center px-4 py-12">
      <SEO title="Connexion" description="Connectez-vous à votre espace Y GO : clients, chauffeurs et partenaires accèdent à leurs réservations, missions et suivi en temps réel." path="/login" />
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B0B0B] via-[#1F1F1F] to-[#0B0B0B] opacity-50" />
      
      {/* Back to home button - top left */}
      <Link 
        to="/" 
        className="absolute top-6 left-6 flex items-center gap-2 text-[#E5E5E5]/60 hover:text-[#C9A24D] transition-colors group"
        data-testid="back-to-home-link"
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
        <span className="text-sm">Retour à l'accueil</span>
      </Link>
      
      <div className="relative w-full max-w-md">
        <h1 className="sr-only" data-testid="login-page-heading">Connexion à votre compte Y GO</h1>
        {/* Logo */}
        <div className="text-center mb-8">
          {logoUrl ? (
            <img src={logoUrl} alt={siteTitle || 'Y GO'} className="h-16 mx-auto mb-2" />
          ) : (
            <div className="font-['Playfair_Display'] text-4xl font-bold text-[#C9A24D] mb-2">
              Y GO
            </div>
          )}
          <p className="text-[#E5E5E5] text-sm tracking-widest uppercase">
            Shuttle Services France
          </p>
        </div>

        <Card className="bg-[#1F1F1F] border-[#C9A24D]/20 shadow-2xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-['Playfair_Display'] text-[#E5E5E5] text-center">
              Connexion
            </CardTitle>
            <CardDescription className="text-[#E5E5E5]/60 text-center">
              Accédez à votre espace personnel
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#E5E5E5]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.fr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#E5E5E5]/40 focus:border-[#C9A24D] focus:ring-[#C9A24D]/20"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#E5E5E5]">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#E5E5E5]/40 focus:border-[#C9A24D] focus:ring-[#C9A24D]/20 pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#E5E5E5]/40 hover:text-[#C9A24D] transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold transition-all duration-300 min-h-[44px] touch-manipulation"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-[#0B0B0B]/30 border-t-[#0B0B0B] rounded-full animate-spin" />
                    Connexion...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Se connecter
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-[#E5E5E5]/10">
              <div className="text-center mb-3">
                <Link 
                  to="/forgot-password" 
                  className="text-[#C9A24D] hover:text-[#E6C77A] transition-colors text-sm"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
              <p className="text-center text-[#E5E5E5]/60 text-sm">
                Pas encore de compte ?{' '}
                <Link 
                  to="/register" 
                  className="text-[#C9A24D] hover:text-[#E6C77A] transition-colors font-medium"
                >
                  Créer un compte
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-[#E5E5E5]/40 text-xs mt-8">
          © 2026 Y GO Shuttle Services France. Tous droits réservés.
        </p>
      </div>

      {/* OTP Verification Modal */}
      <OTPVerificationModal
        isOpen={showOTPModal}
        onClose={() => setShowOTPModal(false)}
        email={otpEmail}
        maskedEmail={maskedEmail}
        onSuccess={handleOTPSuccess}
      />
    </div>
  );
};

export default LoginPage;
